import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { EmptyState, Loading, Panel, Tag } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { fmtInt, fmtUsd, relativeTime } from '../lib/format';

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  // Reset to the first page whenever the search term changes.
  useEffect(() => setPage(1), [q]);

  const { data, loading, error } = useAsync(
    () => adminApi.users({ page, pageSize: 20, q: q || undefined }),
    [page, q],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-cream-400">
          Everyone using Chefty — their saved recipes, import attempts and AI token spend.
        </p>
        <SearchInput value={q} onChange={setQ} placeholder="Search by email…" />
      </div>

      <Panel pad={false} className="overflow-hidden">
        {loading && !data ? (
          <Loading label="Loading users…" />
        ) : error ? (
          <EmptyState title="Couldn’t load users" hint={error} />
        ) : data && data.items.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cream-600/15 text-left text-[0.68rem] uppercase tracking-[0.14em] text-cream-400">
                    <th className="px-5 py-3 font-semibold">User</th>
                    <th className="px-5 py-3 text-right font-semibold">Recipes</th>
                    <th className="px-5 py-3 text-right font-semibold">Imports</th>
                    <th className="px-5 py-3 text-right font-semibold">Tokens</th>
                    <th className="px-5 py-3 text-right font-semibold">Est. cost</th>
                    <th className="px-5 py-3 text-right font-semibold">Last active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-600/10">
                  {data.items.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/users/${u.id}`)}
                      className="cursor-pointer transition-colors hover:bg-saffron-500/[0.04]"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-cream-100">
                            {u.email ?? <span className="text-cream-400">no email</span>}
                          </span>
                          {u.anonymous && <Tag>anon</Tag>}
                        </div>
                        {(u.firstName || u.lastName) && (
                          <div className="text-xs text-cream-400">
                            {[u.firstName, u.lastName].filter(Boolean).join(' ')}
                          </div>
                        )}
                        {/* Stable short id — the only distinguisher for anon / no-email
                            rows (full uuid on hover). */}
                        <code
                          className="mt-0.5 block font-mono text-[11px] text-cream-500"
                          title={u.id}
                        >
                          {u.id.slice(0, 8)}
                        </code>
                      </td>
                      <td className="tnum px-5 py-3 text-right text-cream-200">{fmtInt(u.recipeCount)}</td>
                      <td className="tnum px-5 py-3 text-right text-cream-200">
                        {fmtInt(u.importJobCount)}
                      </td>
                      <td className="tnum px-5 py-3 text-right text-saffron-300">
                        {fmtInt(u.totalTokens)}
                      </td>
                      <td className="tnum px-5 py-3 text-right text-cream-200">
                        {fmtUsd(u.estimatedCostUsd)}
                      </td>
                      <td className="tnum px-5 py-3 text-right text-cream-400">
                        {relativeTime(u.lastActiveAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onPage={setPage}
            />
          </>
        ) : (
          <EmptyState title="No users found" hint={q ? `No match for “${q}”` : undefined} />
        )}
      </Panel>
    </div>
  );
};
